import type { Participant } from '../types';

// NOTE: Borda scoring, average ranks, standard deviations, and consensus
// normalization moved to the canonical engine adapter
// (src/tools/stackRanking.ts) — StackRanking.tsx calls runStackRankingTool.
// Only the participant-pair agreement stat (a UI comparison view concern the
// engine does not model) remains page-local.

/**
 * Calculate agreement between two participants
 */
export function calculateAgreement(
  participant1: Participant,
  participant2: Participant
): number {
  const n = participant1.ranking.length;
  if (n === 0) return 1;

  // Calculate Spearman's rank correlation coefficient
  const ranks1 = new Map<string, number>();
  const ranks2 = new Map<string, number>();

  participant1.ranking.forEach((id, index) => ranks1.set(id, index + 1));
  participant2.ranking.forEach((id, index) => ranks2.set(id, index + 1));

  let sumSquaredDiff = 0;
  participant1.ranking.forEach((id) => {
    const rank1 = ranks1.get(id)!;
    const rank2 = ranks2.get(id) || n + 1; // If item not in participant2's list, assign worst rank
    sumSquaredDiff += Math.pow(rank1 - rank2, 2);
  });

  // Spearman's correlation: 1 - (6 * sum(d^2)) / (n * (n^2 - 1))
  const correlation = 1 - (6 * sumSquaredDiff) / (n * (n * n - 1));

  // Normalize to 0-1 (agreement score)
  return Math.max(0, (correlation + 1) / 2);
}
